import { useState, useEffect, useRef } from "react";
import { Link, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setAlert } from "../../../actions/alert";
import { updateGarage, getGarage } from "../../../actions/admin/garage";
import Alert from "../../layout/Alert";
import Spinner from "../../layout/Spinner";
import AdminPage from "../_components/AdminPage";
import {
  AdminBackLink,
  AdminFormButton,
  AdminFormActions,
} from "../_components/AdminForm";
import ConfirmationModal from "../components/ConfirmationModal";
import SuccessModal from "../components/SuccessModal";
import { ChangeList, diffFields } from "../components/ChangeSummary";
import useSaveConfirmation from "../components/useSaveConfirmation";
import GarageForm, { GARAGE_LIST, GARAGE_FIELDS } from "./GarageForm";

const EMPTY_GARAGE = {
  id: null,
  name: "",
  prefecture: [],
  availableVehicleTypes: [],
  lon: "",
  lat: "",
  isActive: true,
  defaultGarage: false,
};

const UpdateGarage = ({ match }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { garage, error } = useSelector((state) => state.garage);

  // `state.garage.garage` is shared with every other garage screen, so it can still
  // hold the previously opened garage on mount. Only trust it once it actually
  // matches the id in the URL.
  const isCurrentGarage = garage?._id === match.params.id;
  const loadFailed = Boolean(error?.status);

  const [formData, setFormData] = useState(EMPTY_GARAGE);

  // The stored record as the form saw it when it loaded — the "before" side of the
  // change summary.
  const savedFormData = useRef(null);

  useEffect(() => {
    dispatch(getGarage(match.params.id));
  }, [dispatch, match.params.id]);

  useEffect(() => {
    if (!isCurrentGarage) return;

    const loadedFormData = {
      id: garage._id,
      name: garage.name || "",
      prefecture: garage.prefecture || [],
      availableVehicleTypes: (garage.availableVehicleTypes || []).map((e) => ({
        value: e._id,
        label: e.typename,
      })),
      lon: garage.lon ?? "",
      lat: garage.lat ?? "",
      isActive: !!garage.isActive,
      defaultGarage: !!garage.defaultGarage,
    };

    savedFormData.current = loadedFormData;
    setFormData(loadedFormData);
  }, [isCurrentGarage, garage]);

  const save = useSaveConfirmation({
    save: () => dispatch(updateGarage(formData, match.params.id)),
    onDismiss: () => history.push(GARAGE_LIST),
  });

  const onSubmit = (e) => {
    e.preventDefault();

    const changes = diffFields(GARAGE_FIELDS, savedFormData.current || {}, formData);

    if (changes.length === 0) {
      dispatch(setAlert("Nothing to update -- this garage is unchanged.", "primary"));
      return;
    }

    save.request(changes);
  };

  const actions = <AdminBackLink to={GARAGE_LIST} />;

  // Don't build the form (or mount the map) off a garage that isn't loaded yet —
  // reading fields off a null/stale garage is what blanked this screen.
  if (loadFailed || !isCurrentGarage) {
    return (
      <AdminPage title="Update Garage" actions={actions}>
        {loadFailed ? (
          <div className="tw:mt-[24px] tw:rounded-[5px] tw:border tw:border-[#EA4335] tw:bg-[#FDECEA] tw:p-4 tw:text-[15px] tw:text-[#282828]">
            This garage could not be loaded — it may have been deleted.{" "}
            <Link to={GARAGE_LIST}>Back to garage list</Link>
          </div>
        ) : (
          <Spinner />
        )}
      </AdminPage>
    );
  }

  return (
    <AdminPage title="Update Garage" actions={actions}>
      <form onSubmit={onSubmit} className="tw:max-w-[860px]">
        <GarageForm formData={formData} setFormData={setFormData} />

        <AdminFormActions>
          <AdminFormButton type="submit">Update Garage</AdminFormButton>
        </AdminFormActions>
      </form>

      <Alert />

      <ConfirmationModal
        show={!!save.summary}
        title="Apply these garage changes?"
        description="These take effect straight away, on how vehicles are dispatched from here on."
        confirmLabel="Update Garage"
        busyLabel="Saving..."
        isBusy={save.isSaving}
        error={save.error}
        onCancel={save.cancel}
        onConfirm={save.confirm}
      >
        <ChangeList changes={save.summary || []} />
      </ConfirmationModal>

      <SuccessModal
        show={!!save.saved}
        title="Garage updated"
        description="These are live now. Closing this returns you to the garage list."
        onClose={save.dismiss}
      >
        <ChangeList changes={save.saved || []} />
      </SuccessModal>
    </AdminPage>
  );
};

export default UpdateGarage;
